import { claimPendingRecipe } from "authentication/utils";
import { isNullOrUndefined } from "base/utils";
import { supabase } from "lib/supabaseClient";
import React, { createContext, useContext, useEffect, useState } from "react";

const AuthenticationContext = createContext({});

export const useAuthentication = () => {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error(
      "useAuthentication must be used within AuthenticationProvider",
    );
  }
  return context;
};

export const AuthenticationProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkAdminStatus(session.user.id);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkAdminStatus(session.user.id);
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async (userId) => {
    try {
      // Use service role or a custom function to bypass RLS
      const { data, error } = await supabase.rpc("get_user_admin_status", {
        user_id: userId,
      });

      if (!error && !isNullOrUndefined(data)) {
        setIsAdmin(data);
      }
    } catch (err) {
      console.error("Error checking admin status:", err);
      setIsAdmin(false);
    }
  };

  const checkEmailExists = async (email) => {
    const { data, error } = await supabase.rpc("check_email_exists", {
      check_email: email,
    });

    if (error) {
      return false; // If error, assume doesn't exist (fail open)
    }

    return data === true;
  };

  const signUp = async (email, password, displayName) => {
    // Check if email already exists BEFORE attempting signup
    const emailExists = await checkEmailExists(email);

    if (emailExists) {
      throw new Error("User already registered");
    }
    if (!emailExists) {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName,
          },
        },
      });

      if (error) throw error;

      if (data?.session) {
        //if the user added a recipe and opted to log in on add, make sure their newly added recipe is updated
        //to reflect their userId in createdby, thus adding it to their library
        await claimPendingRecipe(data.session.access_token);
      }

      return data;
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) throw error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;
  };

  const value = {
    checkEmailExists,
    isAdmin,
    loading,
    resetPassword,
    signIn,
    signOut,
    signUp,
    updatePassword,
    user,
  };

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  );
};
